import { useRuntimeVariables } from 'app-kit'
import dayjs from 'dayjs'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import type { MathNode } from 'features/math'
import { forecastMath } from 'features/math'
import { ChartDataPoint, DataLayer, TimeRange } from 'features/systemLens'
import {
  performMultivariateLinearRegression,
  performRegression,
  RegressionResult,
} from 'features/timeSeries/regressionHelpers'
import {
  addThresholdDataToChartPoints,
  getEnabledThresholds,
} from 'features/timeSeries/services/thresholdService'
import {
  TimeSeriesDataPoint,
  TimeSeriesService,
} from 'features/timeSeries/timeSeriesService'
import { useTimeSeriesDataCache } from 'features/timeSeries/useTimeSeriesDataCache'
import {
  applyDerivedValues,
  applySmoothingConfigs,
  applyThresholdFilters,
  sanitizeSensorId,
} from 'features/timeSeries/utils/dataProcessing'
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  EnhancedSensorEntry,
  LocalThresholdOverrideType,
  RegressionConfig,
  RolodexEntryType,
  SmoothingConfig,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

import { useDateMode } from './useDateMode'

interface UseTimeSeriesDataProcessingProps {
  sensorEntries: BaseSensorEntry[]
  selectedSensors: BaseSensorEntry[]
  setSelectedSensors: Dispatch<SetStateAction<BaseSensorEntry[]>>
  timeRange: TimeRange
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  dataLayers: DataLayer[]
  siteID: string
  orgID: string
  derivedValueIdsToDeleteRef: MutableRefObject<Set<string>>
  relativeStart: { value?: number; unit: ChartTimeUnit }
  relativeEnd: { isNow: boolean; value?: number; unit: ChartTimeUnit }
}

export const useTimeSeriesDataProcessing = ({
  sensorEntries,
  selectedSensors,
  setSelectedSensors,
  timeRange,
  setTimeRange,
  dataLayers,
  siteID,
  orgID,
  derivedValueIdsToDeleteRef,
  relativeStart,
  relativeEnd,
}: UseTimeSeriesDataProcessingProps) => {
  const {
    parse,
    isFunctionNode,
    isOperatorNode,
    isParenthesisNode,
    isSymbolNode,
  } = forecastMath
  const [chartData, setChartData] = useState<ChartDataPoint[]>([])
  const [rawChartData, setRawChartData] = useState<ChartDataPoint[]>([])
  const [interpolate, setInterpolate] = useState(false)
  const [isProcessingData, setIsProcessingData] = useState(false)
  const [localThresholdOverrides, setLocalThresholdOverrides] =
    useState<LocalThresholdOverrideType>({})
  const { authorizedAxios } = useGeckoAuth()
  const { TIME_SERIES_SERVICE } = useRuntimeVariables()
  const { rolodexPatch } = useRolodexEdit()

  const {
    dateMode,
    setDateMode,
    liveTail,
    setLiveTail,
    cadenceSeconds,
    setCadenceSeconds,
  } = useDateMode({
    setTimeRange,
    timeRange,
    relativeStart,
    relativeEnd,
  })

  // Data processing state
  const [thresholdFilters, setThresholdFilters] = useState<
    Record<string, ThresholdFilterConfig>
  >({})
  const [smoothingConfigs, setSmoothingConfigs] = useState<
    Record<string, SmoothingConfig>
  >({})
  const [derivedValues, internalSetDerivedValues] = useState<
    TimeSeriesDerivedValueEntryJSON[]
  >([])
  const [regressionConfigs, setRegressionConfigs] = useState<
    RegressionConfig[]
  >([])
  const [regressionResults, setRegressionResults] = useState<
    Record<string, RegressionResult>
  >({})
  const [sensorVisibilityConfig, setSensorVisibilityConfig] = useState<
    Record<string, boolean>
  >({})
  const [customSensorColors, setCustomSensorColors] = useState<
    Record<string, string>
  >({})

  const setDerivedValues: Dispatch<
    SetStateAction<TimeSeriesDerivedValueEntryJSON[]>
  > = useCallback(
    (updater) => {
      let newDerivedValues: TimeSeriesDerivedValueEntryJSON[] = []
      internalSetDerivedValues((prev) => {
        newDerivedValues =
          typeof updater === 'function' ? updater(prev) : updater
        return newDerivedValues
      })
      setSelectedSensors((prev) => {
        const selectedSensorIds = new Set(prev.map((s) => s.id))
        newDerivedValues.forEach((dv) => {
          dv.data.dependencyIds.forEach((sensorId) => {
            if (!selectedSensorIds.has(sensorId)) {
              selectedSensorIds.add(sensorId)
            }
          })
        })
        return Array.from(selectedSensorIds)
          .map((id) => sensorEntries.find((s) => s.id === id))
          .filter((s) => s !== undefined)
      })
      setSensorVisibilityConfig((prev) => {
        const newConfig = { ...prev }
        newDerivedValues.forEach((dv) => {
          const sensorId = dv.data.dependencyIds?.[0]
          if (!sensorId || !sensorEntries.find((s) => s.id === sensorId)) return
          newConfig[sensorId] = false
        })
        return newConfig
      })
    },
    [setSelectedSensors, sensorEntries]
  )

  // Initialize data fetching hook
  const { fetchData: fetchTimeSeriesData, isLoading: isLoadingData } =
    useTimeSeriesDataCache({
      debounceMs: 500,
    })

  // Enhanced sensor entries with local threshold overrides
  const enhancedSensorEntries: EnhancedSensorEntry[] = useMemo(() => {
    return sensorEntries.map((sensor) => {
      const override = localThresholdOverrides[sensor.id]
      if (!override) return sensor

      return {
        ...sensor,
        data: {
          ...sensor.data,
          thresholds: override,
        },
      }
    })
  }, [sensorEntries, localThresholdOverrides])

  // Get selected sensor entries for threshold calculations
  const selectedSensorEntries = useMemo(
    () =>
      enhancedSensorEntries.filter((sensor) =>
        selectedSensors.find(
          (selectedSensor) => selectedSensor.id === sensor.id
        )
      ),
    [enhancedSensorEntries, selectedSensors]
  )

  // Calculate enabled thresholds
  const enabledThresholds = useMemo(() => {
    return getEnabledThresholds(selectedSensorEntries)
  }, [selectedSensorEntries])

  // Fetch raw data only when necessary (sensors, time range, etc. change)
  const fetchRawData = useCallback(
    async (options?: { bypassDebounce?: boolean }) => {
      const enabledSensorLayers = dataLayers.filter(
        (layer) => layer.type === 'sensor' && layer.enabled
      )

      if (enabledSensorLayers.length === 0 || selectedSensors.length === 0) {
        setRawChartData([])
        setChartData([])
        return
      }

      setIsProcessingData(true)

      try {
        // Use interval from timeRange if available, otherwise calculate default
        let interval: number
        let interval_unit: ChartTimeUnit

        if (timeRange.interval && timeRange.intervalUnit) {
          interval = timeRange.interval
          interval_unit = timeRange.intervalUnit
        } else {
          const timeRangeSeconds =
            (new Date(timeRange.to).getTime() -
              new Date(timeRange.from).getTime()) /
            1000
          const calculated = TimeSeriesService.getInterval(timeRangeSeconds)
          interval = calculated.interval
          interval_unit = calculated.interval_unit
        }

        const fetchParams = {
          entry_ids: selectedSensors.map((sensor) => sensor.id),
          start_datetime: timeRange.from,
          end_datetime: timeRange.to,
          interval,
          interval_unit: interval_unit,
          interpolate,
        }

        const response = await fetchTimeSeriesData(fetchParams, options)

        const allDataPoints: { [timestamp: string]: ChartDataPoint } = {}
        Object.entries(response).forEach(([sensorId, dataPoints]) => {
          ;(dataPoints as TimeSeriesDataPoint[]).forEach(
            (point: TimeSeriesDataPoint) => {
              const timestamp = point.timestamp
              // convert timestamp to unix milliseconds
              const unixTimestamp = dayjs(timestamp).valueOf()
              if (!allDataPoints[timestamp]) {
                allDataPoints[timestamp] = {
                  timestamp: unixTimestamp,
                  time: dayjs(point.timestamp)
                    .utc()
                    .local()
                    .format('YYYY-MM-DD HH:mm:ss'),
                }
              }
              allDataPoints[timestamp][sensorId] = point.value
            }
          )
        })

        if (dataLayers.find((l) => l.id === 'thresholds')?.enabled) {
          addThresholdDataToChartPoints(allDataPoints, enabledThresholds)
        }

        // Store raw data
        const rawData = Object.values(allDataPoints)
        setRawChartData(rawData)
      } catch (error) {
        console.error('Failed to fetch time series data:', error)
      } finally {
        setIsProcessingData(false)
      }
    },
    [
      dataLayers,
      selectedSensors,
      timeRange,
      fetchTimeSeriesData,
      enabledThresholds,
      interpolate,
    ]
  )

  // Process raw data whenever processing parameters change
  const processData = useCallback(() => {
    if (rawChartData.length === 0) {
      setChartData([])
      return
    }

    // Apply all transformations in the correct order
    let processedData = [...rawChartData]

    // 1. Apply derived values
    processedData = applyDerivedValues(processedData, derivedValues)

    // 2. Apply thresholding
    processedData = applyThresholdFilters(processedData, thresholdFilters)

    // 3. Apply smoothing
    processedData = applySmoothingConfigs(processedData, smoothingConfigs)

    setChartData(processedData)
  }, [rawChartData, derivedValues, thresholdFilters, smoothingConfigs])

  // Memoize regression calculation logic
  const calculateRegression = useCallback(
    async (config: RegressionConfig) => {
      if (!config.enabled || !chartData.length) {
        // Clear result if disabled
        if (!config.enabled && regressionResults[config.id]) {
          setRegressionResults((prev) => {
            const newResults = { ...prev }
            delete newResults[config.id]
            return newResults
          })

          // Also remove the associated derived value
          const derivedValueId = `regression_predicted_${config.id}`
          setDerivedValues((prev) =>
            prev.filter((dv) => dv.id !== derivedValueId)
          )
        }
        return
      }

      // Calculate if we should calculate OR if we don't have a result yet
      if (!config.shouldCalculate && regressionResults[config.id]) {
        return
      }

      try {
        // Handle linear regression with sensor variables (including time + sensors)
        const hasNonTimeVariables = config.xVariables?.some(
          (id) => id !== 'timestamp'
        )
        if (config.regressionType === 'linear' && hasNonTimeVariables) {
          // Linear regression with sensor(s) as independent variable(s)
          const independentSensorIds = config.xVariables
          const dependentSensorId = config.yVariable

          // Filter to get only valid data points where all sensors have values
          const validData = chartData.filter((d) => {
            if (typeof d[dependentSensorId] !== 'number') return false
            return independentSensorIds.every((id) => typeof d[id] === 'number')
          })

          if (validData.length < independentSensorIds.length + 1) {
            console.error('Insufficient data points for regression')
            return
          }

          // Prepare features (independent variables) and values (dependent variable)
          const features = validData.map((d) =>
            independentSensorIds.map((id) =>
              id === 'timestamp'
                ? d[id] - validData[0].timestamp
                : (d[id] as number)
            )
          )

          const values = validData.map((d) => d[dependentSensorId] as number)

          // Perform regression
          const result = await performMultivariateLinearRegression(
            features,
            values,
            TIME_SERIES_SERVICE,
            authorizedAxios
          )

          // Add feature names to the result
          const featureNames = independentSensorIds.map((id) => {
            if (id === 'timestamp') {
              return 'Time'
            }
            const sensor = sensorEntries.find((e) => e.id === id)
            const derived = derivedValues.find((dv) => dv.id === id)
            return sensor
              ? (sensor.data?.name as string) || id
              : derived?.data.name || id
          })

          setRegressionResults((prev) => ({
            ...prev,
            [config.id]: {
              ...result,
              featureNames,
              predictions: [], // No time-based predictions for sensor-based regression
            },
          }))

          // Create a derived value for the regression prediction
          const derivedValueId = `regression_predicted_${config.id}`

          // Get the dependent variable name
          const dependentSensor = sensorEntries.find(
            (e) => e.id === config.yVariable
          )
          const dependentName = dependentSensor
            ? dependentSensor.data?.name || config.yVariable
            : config.yVariable

          const derivedValueName = `Predicted ${dependentName}`

          // Build the expression using the regression coefficients
          // For multivariate linear regression: y = β₀ + β₁x₁ + β₂x₂ + ... + βₙxₙ
          const expressionParts: string[] = []

          // Add intercept (β₀)
          expressionParts.push(result.coefficients[0].toString())

          // Add terms for each feature
          independentSensorIds.reverse().forEach((sensorId, index) => {
            const coefficient = result.coefficients[index + 1]
            if (coefficient !== 0) {
              const variableName = sensorId
                .replaceAll('-', '_')
                .replaceAll('.', '_')
              const term =
                coefficient >= 0
                  ? `+ ${coefficient} * ${variableName}`
                  : `- ${Math.abs(coefficient)} * ${variableName}`
              expressionParts.push(term)
            }
          })

          const expression = expressionParts.join(' ')
          // Update or create the derived value
          setDerivedValues((prev) => {
            // Remove any existing regression prediction for this config
            const filtered = prev.filter((dv) => dv.id !== derivedValueId)

            // Add the new derived value
            return [
              ...filtered,
              {
                id: derivedValueId,
                type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
                data: {
                  name: derivedValueName,
                  expression,
                  description: `Multivariate linear regression prediction: ${result.equation}`,
                  unit: undefined,
                  dependencyIds: independentSensorIds,
                },
                tags: {},
                updatedAt: undefined,
                createdAt: undefined,
                lastUpdatedBy: undefined,
              },
            ]
          })
        } else {
          // Filter to get only valid timestamp-value pairs
          const validData = chartData
            .filter(
              (d) => d.timestamp > 0 && typeof d[config.yVariable] === 'number'
            )
            .map((d) => ({
              timestamp: d.timestamp as number,
              value: d[config.yVariable] as number,
            }))

          const timestamps = validData.map((d) => d.timestamp)
          const values = validData.map((d) => d.value)

          if (validData.length === 0) {
            return
          }

          const result = await performRegression(
            timestamps,
            values,
            config,
            TIME_SERIES_SERVICE,
            authorizedAxios
          )

          setRegressionResults((prev) => ({
            ...prev,
            [config.id]: result,
          }))
        }

        // Reset shouldCalculate flag after calculation
        if (config.shouldCalculate) {
          setRegressionConfigs((prev) =>
            prev.map((c) =>
              c.id === config.id ? { ...c, shouldCalculate: false } : c
            )
          )
        }
      } catch (error) {
        console.error('Regression calculation failed:', error)
        setRegressionResults((prev) => {
          const newResults = { ...prev }
          delete newResults[config.id]
          return newResults
        })
      }
    },
    [
      chartData,
      sensorEntries,
      derivedValues,
      regressionResults,
      authorizedAxios,
      TIME_SERIES_SERVICE,
      setDerivedValues,
    ]
  )

  // Serialize enabledThresholds for stable comparison
  const enabledThresholdsKey = useMemo(
    () =>
      JSON.stringify(
        enabledThresholds.map((t) => ({
          sensorId: t.sensorId,
          ll: t.lowerLowerThreshold,
          l: t.lowerThreshold,
          u: t.upperThreshold,
          uu: t.upperUpperThreshold,
        }))
      ),
    [enabledThresholds]
  )

  // Fetch raw data when key dependencies change
  useEffect(() => {
    fetchRawData()
  }, [
    selectedSensors,
    timeRange.from,
    timeRange.to,
    timeRange.interval,
    timeRange.intervalUnit,
    interpolate,
    dataLayers,
    enabledThresholdsKey,
    liveTail,
    fetchRawData,
  ])

  // Process data whenever raw data or processing parameters change
  useEffect(() => {
    processData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawChartData, derivedValues, thresholdFilters, smoothingConfigs])

  // Handle regression calculations
  useEffect(() => {
    // Only process configs that need calculation
    regressionConfigs.forEach((config) => {
      // Skip if already calculated and shouldCalculate is false
      if (
        !config.shouldCalculate &&
        regressionResults[config.id] &&
        config.enabled
      ) {
        return
      }
      calculateRegression(config)
    })
  }, [regressionConfigs, calculateRegression, regressionResults])

  const unitMapping = useMemo(() => {
    const units: Record<string, string> = {}
    sensorEntries?.forEach((entry) => {
      const unit = entry.data.unit
      if (entry.data.name) {
        // Use 'No Unit' for sensors without a unit
        units[entry.data.name] = unit || 'No Unit'
      }
    })
    return units
  }, [sensorEntries])

  const handleRemoveDerivedValue = useCallback(
    async (id: string) => {
      // Find all derived values that need to be removed (including cascading dependents)
      const idsToRemove = new Set<string>([id])

      const findDependents = (targetId: string) => {
        derivedValues.forEach((dv) => {
          if (
            !idsToRemove.has(dv.id) &&
            dv.data.dependencyIds?.includes(targetId)
          ) {
            idsToRemove.add(dv.id)
            // Recursively check if other derived values depend on this one
            findDependents(dv.id)
          }
        })
      }

      findDependents(id)

      // Add to list of IDs to delete on save
      idsToRemove.forEach((idToRemove) => {
        derivedValueIdsToDeleteRef.current.add(idToRemove)
      })

      // Remove from local state - actual deletion happens on chart save
      const updatedDerivedValues = derivedValues.filter(
        (dv) => !idsToRemove.has(dv.id)
      )
      setDerivedValues(updatedDerivedValues)
    },
    [derivedValues, setDerivedValues, derivedValueIdsToDeleteRef]
  )

  const handleChangeColor = useCallback((sensorId: string, color: string) => {
    setCustomSensorColors((prev) => ({
      ...prev,
      [sensorId]: color,
    }))
  }, [])

  const createDerivedValues = useCallback(
    async (
      newDerived: TimeSeriesDerivedValueEntryJSON,
      editingDerivedId: string | null
    ) => {
      if (!newDerived.data.name || !newDerived.data.expression) {
        return derivedValues
      }

      // Parse and validate the expression using math.js
      const parsedExpression = parse(newDerived.data.expression)
      let variables: string[] = []

      const extractVariables = (node: MathNode): void => {
        if (isSymbolNode(node)) {
          variables.push(node.name)
        }
        if ((isFunctionNode(node) || isOperatorNode(node)) && node.args) {
          node.args.forEach(extractVariables)
        }
        if (isParenthesisNode(node)) {
          extractVariables(node.content)
        }
      }
      extractVariables(parsedExpression)
      // Remove duplicates
      variables = [...new Set(variables)]

      // Build the list of dependency IDs from the variables
      const dependencyIds: string[] = []
      variables.forEach((varName) => {
        const derivedVar = derivedValues.find(
          (dv) => sanitizeSensorId(dv.id) === varName
        )
        if (derivedVar) {
          dependencyIds.push(derivedVar.id)
        } else {
          const sensor = sensorEntries.find(
            (e) => sanitizeSensorId(e.id) === varName
          )
          if (sensor) {
            dependencyIds.push(sensor.id)
          }
        }
      })

      const derivedValue: TimeSeriesDerivedValueEntryJSON = {
        id: editingDerivedId || `derived_${Date.now()}`,
        type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
        data: {
          name: newDerived.data.name,
          expression: newDerived.data.expression,
          unit: newDerived.data.unit,
          description: newDerived.data.description,
          dependencyIds,
        },
        tags: {
          site: siteID,
        },
      }

      await rolodexPatch([
        {
          id: derivedValue.id,
          type: derivedValue.type,
          data: {
            name: derivedValue.data.name,
            expression: derivedValue.data.expression,
            unit: derivedValue.data.unit,
            description: derivedValue.data.description,
            dependency_ids: dependencyIds,
          },
          tags: {
            site: siteID,
            organization: orgID,
          },
        },
      ])

      if (editingDerivedId) {
        return derivedValues.map((dv) =>
          dv.id === editingDerivedId ? derivedValue : dv
        )
      } else {
        return [...derivedValues, derivedValue]
      }
    },
    [
      siteID,
      orgID,
      rolodexPatch,
      derivedValues,
      sensorEntries,
      parse,
      isFunctionNode,
      isOperatorNode,
      isParenthesisNode,
      isSymbolNode,
    ]
  )

  // Combined loading state for UI
  const isLoading = isLoadingData || isProcessingData

  return {
    // Data state
    chartData,
    setChartData,
    rawChartData,
    interpolate,
    setInterpolate,
    isLoading,
    liveTail,
    setLiveTail,

    // Processing state
    thresholdFilters,
    setThresholdFilters,
    smoothingConfigs,
    setSmoothingConfigs,
    derivedValues,
    setDerivedValues,
    regressionConfigs,
    setRegressionConfigs,
    regressionResults,
    setRegressionResults,
    localThresholdOverrides,
    setLocalThresholdOverrides,
    sensorVisibilityConfig,
    setSensorVisibilityConfig,
    dateMode,
    setDateMode,
    customSensorColors,
    setCustomSensorColors,
    cadenceSeconds,
    setCadenceSeconds,

    // Computed values
    unitMapping,

    // Functions
    fetchRawData,
    handleRemoveDerivedValue,
    createDerivedValues,
    handleChangeColor,
  }
}
